import React, { useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Select, useSelect, Skeleton, Typography } from '@chilecompra/react-kit'
import SpiderChart from '../../components/Charts/SpiderChart'
import {
  ContainerCard as Card,
  DimensionsChartContainer,
  DimensionsChartContent,
  ChartColumn,
  FiltersColumn,
  AverageScoreCard,
  AverageScoreValue,
  AverageScoreLabel,
  DimensionsChartFooter,
  FooterText,
  Strong
} from './BehaviorSection.styles'
import { BASE_SERIE, SPIDER_CHART_OPTIONS } from './BehaviorSection.constants'
import { calculateSeriesData, getLastValidMonthValue } from './BehaviorSection.utils'
import WarningMessage from './BehaviorSection.WarningMessage'
import EmptyCardMessage from './BehaviorSection.EmptyCardMessage'

/**
 * The Dimensions Chart component.
 * Displays the behavior index dimensions with filters and average score.
 */
const DimensionsChart = ({
  dimensions,
  dimensionsError,
  averageScore,
  updatedAt,
  yearsOptions,
  monthsOptions,
  yearsOptionsLoading,
  monthsOptionsLoading,
  paramaters,
  onYearChange,
  onMonthChange,
  showFilters,
  defaultAverageScore
}) => {
  const { yearType, monthType } = paramaters || {}
  const customOptions = SPIDER_CHART_OPTIONS('Dimensiones del Índice de Gestión', true)
  const [yearHasChanged, setYearHasChanged] = useState(false)

  const score = useMemo(() => {
    if (dimensions?.data?.length > 0) {
      return averageScore.toFixed(2).replace('.', ',')
    }

    if (typeof defaultAverageScore === 'number') {
      return defaultAverageScore.toFixed(2).replace('.', ',')
    }

    return '0,00'
  }, [averageScore, defaultAverageScore, dimensions?.data?.length])

  const {
    error: yearTypeError,
    value: yearTypeValue,
    onChange: handleYearTypeChange
  } = useSelect({
    initialValue: yearType,
    changeCallback: updatedValue => {
      setYearHasChanged(true)
      onYearChange({ name: 'yearType', value: updatedValue })
    }
  })

  const {
    error: monthTypeError,
    value: monthTypeValue,
    setValue: setMonthTypeValue,
    onChange: handleMonthTypeChange
  } = useSelect({
    initialValue: monthType,
    changeCallback: updatedValue => {
      onMonthChange({ name: 'monthType', value: updatedValue })
    }
  })

  const seriesData = calculateSeriesData(dimensions?.data, true, dimensions?.weights)

  useEffect(() => {
    if (yearTypeValue && !monthsOptionsLoading && monthsOptions.length > 0 && yearHasChanged) {
      const value = getLastValidMonthValue(monthsOptions)
      if (value !== undefined) {
        setYearHasChanged(false)
        setMonthTypeValue(value)
        onMonthChange({ name: 'monthType', value })
      }
    }
  }, [yearTypeValue, monthsOptionsLoading, monthsOptions, onMonthChange, yearHasChanged, setMonthTypeValue])

  useEffect(() => {
    if (monthType && monthsOptions.length > 0) {
      const value = getLastValidMonthValue(monthsOptions)
      if (value !== undefined && value < monthType) {
        setMonthTypeValue(value)
        onMonthChange({ name: 'monthType', value })
      }
    }
  }, [monthsOptions, monthType])

  if (dimensionsError) {
    return <WarningMessage message={dimensionsError} />
  }

  return (
    <Card padding="24px 24px 14px 24px">
      <DimensionsChartContainer>
        {dimensions?.data?.length > 0 && (
          <Typography fontWeight="bold" padding="0" variant="h4">
            Dimensiones del Índice de Gestión
          </Typography>
        )}

        <DimensionsChartContent>
          <ChartColumn>
            {!dimensions || dimensions?.data?.length === 0 ? (
              <EmptyCardMessage onlyTextAndImage />
            ) : (
              <SpiderChart
                id="dimensions-chart"
                title=""
                categories={dimensions?.categories || []}
                series={[
                  {
                    ...BASE_SERIE,
                    data: seriesData
                  }
                ]}
                customOptions={customOptions}
              />
            )}
          </ChartColumn>

          {showFilters && (
            <FiltersColumn>
              {yearsOptionsLoading ? (
                <Skeleton height="56px" width="100%" />
              ) : (
                <Select
                  error={yearTypeError}
                  label="Año"
                  options={yearsOptions}
                  value={yearTypeValue}
                  onChange={handleYearTypeChange}
                />
              )}

              {yearsOptionsLoading || monthsOptionsLoading ? (
                <Skeleton height="56px" width="100%" />
              ) : (
                <Select
                  error={monthTypeError}
                  label="Mes de Gestión"
                  options={monthsOptions}
                  value={monthTypeValue}
                  onChange={handleMonthTypeChange}
                />
              )}

              <AverageScoreCard enableBorder={dimensions?.data?.length > 0}>
                <AverageScoreValue>
                  {dimensions?.data?.length > 0 && (
                    <>
                      {score}/<span>5</span>{' '}
                    </>
                  )}
                </AverageScoreValue>
                <AverageScoreLabel>
                  {dimensions?.data?.length > 0 && (
                    <>
                      Nota correspondiente al <Strong>promedio ponderado mensual</Strong> de las dimensiones del Índice
                      de Gestión.
                    </>
                  )}
                </AverageScoreLabel>
              </AverageScoreCard>
            </FiltersColumn>
          )}
        </DimensionsChartContent>

        <DimensionsChartFooter>
          <FooterText>
            <Strong>Última actualización:</Strong> {updatedAt}
          </FooterText>
          <FooterText>
            <Strong>Fuente:</Strong> Información obtenida desde Mercado Público.
          </FooterText>
        </DimensionsChartFooter>
      </DimensionsChartContainer>
    </Card>
  )
}

DimensionsChart.propTypes = {
  /** Dimensions data with categories, data values, and weights */
  dimensions: PropTypes.shape({
    categories: PropTypes.arrayOf(PropTypes.string),
    data: PropTypes.arrayOf(PropTypes.number),
    weights: PropTypes.arrayOf(PropTypes.number)
  }),
  /** Dimensions error */
  dimensionsError: PropTypes.string,
  /** Average score value */
  averageScore: PropTypes.number,
  /** Last update date string */
  updatedAt: PropTypes.string,
  /** Year options for selector */
  yearsOptions: PropTypes.array,
  /** Month options for selector */
  monthsOptions: PropTypes.array,
  /** Loading state for years */
  yearsOptionsLoading: PropTypes.bool,
  /** Loading state for months */
  monthsOptionsLoading: PropTypes.bool,
  /** Current filter parameters */
  paramaters: PropTypes.shape({
    yearType: PropTypes.number,
    monthType: PropTypes.number
  }),
  /** Callback when year changes */
  onYearChange: PropTypes.func,
  /** Callback when month changes */
  onMonthChange: PropTypes.func,
  showFilters: PropTypes.bool,
  defaultAverageScore: PropTypes.number
}

DimensionsChart.defaultProps = {
  dimensions: { categories: [], data: [], weights: [] },
  dimensionsError: undefined,
  averageScore: 0,
  updatedAt: '',
  yearsOptions: [],
  monthsOptions: [],
  yearsOptionsLoading: false,
  monthsOptionsLoading: false,
  paramaters: {},
  onYearChange: () => {},
  onMonthChange: () => {},
  showFilters: true,
  defaultAverageScore: 0
}

export default DimensionsChart
